import { Box, SxProps, Theme } from '@mui/material'
import SelectionBox, {
  Props as SelectionBoxProps,
} from 'components/form/SelectionBox'
import { useMemo } from 'react'
import { FieldValues } from 'react-hook-form'

interface Props<T> extends SelectionBoxProps<T> {
  itemColor?: string
  itemDefinition?: keyof T
  customSelectionWidth?: string
}

//eslint-disable-next-line @typescript-eslint/no-explicit-any
const RatingSelection = <T extends Record<string, any>>({
  defaultOption = 'Select One',
  defaultValue,
  disabled,
  label,
  hideDefaultOption,
  hideLabel,
  hideErrorMessage = false,
  items,
  itemColor = 'backgroundColorHex',
  itemDefinition,
  itemLabel = 'name',
  itemValue = 'id',
  readonly,
  useFormHook,
  rules,
  customSelectionWidth,
}: Props<T>): JSX.Element => {
  const ratingContent = (item: T, _field: FieldValues) => {
    return (
      <Box sx={styles.container}>
        <Box
          display="flex"
          alignItems="center"
          width={!itemDefinition ? '-webkit-fill-available' : 'fit-content'}
        >
          <Box
            sx={{
              backgroundColor: item[itemColor] ?? 'white',
              ...styles.dot,
            }}
          />
          <Box sx={Object.assign({}, styles.mx, styles.ellipsis)}>
            {item[itemLabel]}
          </Box>
        </Box>
        {itemDefinition && item[itemDefinition] && (
          <Box color="text.secondary" sx={styles.ellipsis}>
            {item[itemDefinition]}
          </Box>
        )}
      </Box>
    )
  }

  const selectedContent = ({ item }: { item: T; field: FieldValues }) => {
    return (
      <Box sx={styles.container}>
        <Box
          sx={{
            backgroundColor: item[itemColor] ?? '',
            ...Object.assign({}, styles.colorText, styles.ellipsis),
          }}
          minWidth={itemDefinition ? 'min-content' : ''}
        >
          {item[itemLabel]}
        </Box>
        {itemDefinition && item[itemDefinition] && (
          <Box
            color="text.secondary"
            sx={styles.ellipsis}
            width="fill-available"
          >
            {item[itemDefinition]}
          </Box>
        )}
      </Box>
    )
  }

  const ratingItems: Array<
    T & { rating: (item: T, field: FieldValues) => JSX.Element }
  > = useMemo(() => {
    return items.map((item) => ({
      ...item,
      rating: (item, field) => ratingContent(item, field),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  return (
    <SelectionBox
      defaultOption={defaultOption}
      defaultValue={defaultValue}
      disabled={disabled}
      hideDefaultOption={hideDefaultOption}
      hideLabel={hideLabel}
      hideErrorMessage={hideErrorMessage}
      items={ratingItems}
      itemLabel="rating"
      itemValue={itemValue}
      label={label}
      readonly={readonly}
      renderValue={selectedContent}
      rules={rules}
      useFormHook={useFormHook}
      customSelectionWidth={customSelectionWidth}
    />
  )
}

const styles: Record<string, SxProps<Theme>> = {
  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  dot: {
    borderRadius: '50%',
    height: 8,
    width: 8,
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mx: {
    m: (theme) => theme.spacing(0, 1),
  },
  colorText: {
    color: '#0A0045',
    borderRadius: '4px',
    m: (theme) => theme.spacing(0, 1),
    p: (theme) => theme.spacing(0, 1),
  },
}

export default RatingSelection
